import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationDialogComponent } from '@app/components';
import { Drive } from '@app/models/documents/directory/drive.model';
import { Folder } from '@app/models/documents/directory/folder.model';
import { Node } from '@app/models/documents/directory/node.model';
import { NodeInTreeFormat } from '@app/modules/documents-v2/interfaces/node-in-tree-format.interface';
import { DocumentFoldersService } from '@app/modules/documents-v2/services/document-folders.service';
import { PlatformModule } from '@app/platform.module';
import { NotifyService } from '@app/services';
import { breakPoints } from '@app/styles/theme';
import { DriveTypeDisplayOptions } from '@app/types/translatables/drive-name-display.options';
import { DocumentMoveNodeService } from '@documents-v2/services';
import { TranslateModule } from '@ngx-translate/core';
import { DriveTypeEnum, SystemFolderNamesEnum } from '../../enums';

const findAndRemoveNode = (root: NodeInTreeFormat, nodeId: number, parent: NodeInTreeFormat, index: number): void => {
    if (root.nodeId === nodeId) {
        if (parent.children) {
            parent.children.splice(index, 1);
        } else return;
    }

    if (root.children) {
        for (const [index, child] of root.children.entries()) {
            findAndRemoveNode(child, nodeId, root, index);
        }
    }
};

@Component({
    standalone: true,
    imports: [PlatformModule, CommonModule, TranslateModule, FormsModule],
    selector: 'app-document-folders-sidebar-v2',
    templateUrl: './document-folders-sidebar.template.html',
    styleUrls: ['./document-folders-sidebar.style.scss'],
})
export class DocumentFoldersSidebarComponent {
    @Input() tableParentNode: Node = new Node();
    @Input() allowDragAndDrop = true;
    @Input() showMeatballs = true;
    @Input() deselectEverything = false;

    @Output() loadTableBasedOnSelectedNode: EventEmitter<Node> = new EventEmitter<Node>();
    @Output() reloadTableOnSideBarActions: EventEmitter<void> = new EventEmitter<void>();

    // (mobile view only)
    @Input() inRecentDocumentsState = false;
    @Input() isIncompleteView = false;
    @Output() recentDocumentsClicked: EventEmitter<void> = new EventEmitter<void>();
    @Output() incompleteDocumentsClicked: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('publicDataForbiddenDialog', { static: true }) publicDataForbiddenDialog!: ConfirmationDialogComponent;
    @ViewChild('privateViewOnlyDocForbiddenDialog', { static: true })
    privateViewOnlyDocForbiddenDialog!: ConfirmationDialogComponent;
    @ViewChild('moveErrorResult', { static: true }) moveErrorResult!: ConfirmationDialogComponent;

    isLoading = true;

    driveTypeEnum = DriveTypeEnum;
    driveTypeDisplay = DriveTypeDisplayOptions;

    _sidebarDrives: Drive[] = [];
    sidebarData: NodeInTreeFormat[] = [];
    sidebarDataLookup: NodeInTreeFormat[] = [];

    moveConflicts: string[] = [];
    moveErrorHeader: string = '';
    moveErrorDescription: string = '';

    @Input()
    set sidebarDrives(value: Drive[]) {
        if (value) {
            this._sidebarDrives = value;
            this.sidebarData = [];
            this.reloadSidebar();
        }
    }
    get sidebarDrives(): Drive[] {
        return this._sidebarDrives;
    }

    get isMobile(): boolean {
        return !this.breakpointObserver.isMatched(`(min-width: ${breakPoints.lg}px)`);
    }

    showWarningDialogOnMove = false;
    folderRenameDialogOpen = false;
    deleteFolderDialogOpen = false;

    nodeToRename: NodeInTreeFormat | undefined;
    folderRename: string = '';

    nodeToDelete: NodeInTreeFormat | undefined;

    depth = 0;

    constructor(
        private notify: NotifyService,
        private documentFoldersService: DocumentFoldersService,
        private breakpointObserver: BreakpointObserver,
        private documentMoveNodeService: DocumentMoveNodeService
    ) {}

    async loadTable(nodeId: number): Promise<void> {
        const node: Node = await Node.with(['source']).find(nodeId);
        this.loadTableBasedOnSelectedNode.emit(node);
    }

    toggleNodeExpanded(node: NodeInTreeFormat): void {
        node.expanded = !node.expanded;
        // keep lookup state in sync
        this.sidebarDataLookup[node.nodeId].expanded = node.expanded;
    }

    /**
     * Node or nodes (or tree version) dropped on sidebar - will check that it can move the node, move it, then reload
     * both itself and the table.
     */
    async onDrop(moved: { data: Node | Node[] | NodeInTreeFormat }, target: NodeInTreeFormat): Promise<void> {
        const { nodes, nodeIds } = await this.documentMoveNodeService.extractNodes(moved.data);

        // If nodeIds contain targetId, reject move action
        if (nodeIds.includes(target.nodeId)) {
            this.notify.error('document.cannotMoveIntoSelf');
            return;
        }

        await this.moveNodeToTarget(nodeIds, nodes, target);
    }

    getNodeIcon(node: NodeInTreeFormat): string {
        if (node.isSystemFolder === true) {
            switch (node.name) {
                case SystemFolderNamesEnum.assigned:
                    return 'folderSign';
                case SystemFolderNamesEnum.upload:
                    return 'folderUpload';
                case SystemFolderNamesEnum.tasks:
                case SystemFolderNamesEnum.training:
                    return 'folderLock';
            }
        }

        if (node.driveType === DriveTypeEnum.private || node.driveType === DriveTypeEnum.employee) {
            return 'folderPrivate';
        }

        if (node.driveType === DriveTypeEnum.public) {
            return 'folderShared';
        }

        return '';
    }

    getNodeDisplayName(node?: NodeInTreeFormat): string {
        if (node == undefined) {
            return '';
        }

        // Compose the translation key if the node is a system folder
        if (node.isSystemFolder) {
            const folderNameKey = Object.entries(SystemFolderNamesEnum).find(
                ([key, value]) => value === node.name
            )?.[0];
            return folderNameKey ? 'document.systemFolders.' + folderNameKey : node.name;
        }
        return node.name;
    }

    /**
     * This method is move node from current parent node to new parent node within frontend before making
     * an API call by resetting `parentNodeId` of `draggedNode` and add it to children of `targetNode`
     * i.e. new parent node.
     */
    async moveNodeToTarget(childNodeIds: number[], childNodes: Node[], target: NodeInTreeFormat): Promise<void> {
        this.showWarningDialogOnMove = true;

        const isMoveValid = await this.documentMoveNodeService.validateMove(
            childNodes,
            target.nodeId,
            this.privateViewOnlyDocForbiddenDialog,
            this.publicDataForbiddenDialog
        );

        if (isMoveValid) {
            this.apiCallToMoveNode(childNodeIds, target);
        }
    }

    async onRenameFolder(): Promise<void> {
        try {
            //  Fix TS compiler error: Object is possibly undefined
            // 'throw' of exception caught locally
            if (!this.nodeToRename) {
                throw new Error();
            }

            const node: Node = await Node.with(['source']).find(this.nodeToRename.nodeId);
            const folderToUpdate: Folder = node.source;

            folderToUpdate.name = this.folderRename;

            await folderToUpdate.save();
            this.nodeToRename.name = this.folderRename;

            // Reload table
            if (
                this.nodeToRename.nodeId === this.tableParentNode.id ||
                this.nodeToRename.parentNodeId === this.tableParentNode.id
            ) {
                this.reloadTableOnSideBarActions.emit();
            }

            this.notify.success('document.folderRenamedSuccessfully');
        } catch (e) {
            this.notify.error('document.unableToRenameFolder');
        } finally {
            this.folderRenameDialogOpen = false;
        }
    }

    async onDeleteFolder(): Promise<void> {
        try {
            //  Fix TS compiler error: Object is possibly undefined
            // 'throw' of exception caught locally
            if (!this.nodeToDelete) {
                throw new Error();
            }

            const node: Node = await Node.with(['source']).find(this.nodeToDelete.nodeId);
            const folderToDelete: Folder = node.source;

            const reloadTableOnDelete =
                this.nodeToDelete.nodeId === this.tableParentNode.id ||
                this.nodeToDelete.parentNodeId === this.tableParentNode.id;

            await folderToDelete.delete();

            this.notify.success({
                key: 'document.folderDeletedSuccessfully',
                params: {
                    folderName: this.nodeToDelete.name,
                },
            });

            // Remove node from my frontend data source
            const dataSource = this.sidebarData.filter((drive) => drive.driveType === this.nodeToDelete?.driveType);
            findAndRemoveNode(dataSource[0], this.nodeToDelete.nodeId, dataSource[0], 0);

            // Reload table
            if (reloadTableOnDelete) {
                this.reloadTableOnSideBarActions.emit();
            }
        } catch (e) {
            this.notify.error('document.unableToDeleteFolder');
        } finally {
            this.deleteFolderDialogOpen = false;
        }
    }

    /**
     * Reloads sidebar based on table actions such as create, rename or delete folders
     * and restores expanded state of nodes.
     */
    reloadSidebar(): void {
        this.isLoading = true;
        const newSidebarData: NodeInTreeFormat[] = [];

        const promises = [];
        for (let i = 0; i < this.sidebarDrives.length; i++) {
            const currentDrive: Drive = this.sidebarDrives[i];

            const promise = this.getFoldersBasedOnDriveNode(currentDrive.node).then(
                (folderNodes: NodeInTreeFormat[] | undefined) => {
                    if (!folderNodes) {
                        return;
                    }

                    // Set drive and folders in tree format and add it to sidebar data
                    const driveNode: NodeInTreeFormat = {
                        driveType: currentDrive.type,
                        isSystemFolder: false,
                        name: currentDrive.node.name,
                        parentNodeId: null,
                        nodeId: currentDrive.node.id,
                        children: [],
                    };

                    driveNode.children = folderNodes;

                    // The following code will add public drive to the bottom of the array
                    if (driveNode.driveType === DriveTypeEnum.public) {
                        newSidebarData.push(driveNode);
                    } else {
                        newSidebarData.unshift(driveNode);
                    }
                }
            );
            promises.push(promise);
        }
        Promise.all(promises).then(() => {
            this.sidebarDataLookup = this.buildSidebarDataLookup(newSidebarData, true, true);
            this.sidebarData = newSidebarData;
            this.isLoading = false;
        });
    }

    expandParentNode(nodeId: number | null): void {
        if (!nodeId) {
            return;
        }
        for (const key in this.sidebarDataLookup) {
            if (key == nodeId?.toString()) {
                this.sidebarDataLookup[key].expanded = true;
                this.expandParentNode(this.sidebarDataLookup[nodeId]?.parentNodeId);
                return;
            }
        }
    }

    onRecentDocuments(): void {
        this.recentDocumentsClicked.emit();
    }

    hasRecentDocumentsListener(): boolean {
        return this.recentDocumentsClicked.observers.length > 0;
    }

    onIncompleteDocument(): void {
        this.incompleteDocumentsClicked.emit();
    }

    hasIncompleteDocumentsListener(): boolean {
        return this.incompleteDocumentsClicked.observers.length > 0;
    }

    private async getFoldersBasedOnDriveNode(driveNode: Node): Promise<NodeInTreeFormat[] | undefined> {
        try {
            return await this.documentFoldersService.getChildrenInTreeFormat(driveNode);
        } catch (err) {
            this.notify.error('document.cannotFetchFolders');
        }
    }

    /**
     * build a flat array of all NodeInTreeFormat
     **/
    private buildSidebarDataLookup(
        nodes: NodeInTreeFormat[] | null,
        setDefaultExpandedProperty = false,
        preserveOpenState = true,
        result: NodeInTreeFormat[] = []
    ): NodeInTreeFormat[] {
        nodes?.forEach((node: NodeInTreeFormat) => {
            /**
             * This expanded flag is important, it ensures that the sidebar stays the same when reloading.
             * Having it in this method is not ideal, as it's not directly related, but it makes it superfast as we're
             * already walking the tree to build the lookup cache.
             */
            if (preserveOpenState) {
                node.expanded = this.sidebarDataLookup[node.nodeId]?.expanded;
            }

            result[node.nodeId] = node;

            result = this.buildSidebarDataLookup(node.children, setDefaultExpandedProperty, preserveOpenState, result);
        });

        return result;
    }

    /**
     * This method is make an api call to move nodes from one parent node to another parent node.
     */
    private async apiCallToMoveNode(childNodeIds: number[], parent: NodeInTreeFormat): Promise<void> {
        // Do not allow moving a node to itself
        if (childNodeIds.includes(parent.nodeId)) {
            this.notify.error('document.cannotMoveIntoSelf');
            return;
        }

        return this.documentFoldersService
            .moveNode(childNodeIds, parent.nodeId)
            .then(() => {
                this.notify.success('document.movedItemSuccessfully');
                this.reloadTableOnSideBarActions.emit();
                this.reloadSidebar();
            })
            .catch((err: HttpErrorResponse) => {
                const error = this.documentMoveNodeService.parseError(err);

                if (error.moveConflicts.length > 0) {
                    this.moveErrorHeader = error.header;
                    this.moveErrorDescription = error.description;
                    this.moveConflicts = error.moveConflicts;
                    this.moveErrorResult.open().then(() => (this.moveConflicts = []));
                }
            });
    }
}
